import _ from 'lodash';
import axios, { AxiosRequestConfig } from 'axios';

import { createSource, http, SourceGroup } from '../helpers/axios';
import { store } from 'store/createStore';

const parseErrorMessage = (data, fallback) => {
  if (typeof data === 'string') {
    return data;
  }

  if (typeof data === 'object') {
    data = parseErrorMessage(data.message) || parseErrorMessage(data.error);
  }

  return data || fallback;
};

/**
 * Base function to make a request to server.
 *
 * @param {AxiosRequestConfig} config axios config, i.e. { url, method, params, data ... }
 * @param {SourceGroup} sources optional, but strongly recommended.
 * A SourceGroup object to track all outstanding requests and cancel them if
 * the container component is unmounted.
 * @param {string} label optional request identifier.
 *
 * @returns a promise that resolves with the response data
 * { data, status, error }
 */
const request = async (config, sources, label) => {
  const source = createSource();

  try {
    if (sources instanceof SourceGroup) {
      sources.add(source, label);
    } else {
      console.warn(
        'ATTENTION, PROVIDE A SourceGroup OBJECT FOR YOUR REQUEST TO AVOID MEMORY LEAKS!',
        config.url
      );
    }

    const res = await http.request({ ...config, cancelToken: source.token });
    return { data: res.data, status: _.toNumber(res.status) };
  } catch (error) {
    if (axios.isCancel(error)) {
      return { canceled: true, status: 400 };
    }

    const { response, request, message } = error;

    return response
      ? {
          error: parseErrorMessage(response.data, message),
          status: _.toNumber(response.status),
          errorData: response.data,
        }
      : request
      ? {
          error: parseErrorMessage(request.response, message),
          status: _.toNumber(request.status),
        }
      : { error: message, status: 400 };
  } finally {
    if (sources instanceof SourceGroup) {
      sources.remove(source);
    }
  }
};

const getUserInteractions = (query, sources) => {
  const {
    users,
    from,
    to,
    per_page,
    page,
    userAnonymous,
    type,
    sort,
    hideDeletedUsers,
    filterReferral,
  } = query;

  let filter = {};

  if (users) {
    filter._id = users;
  }

  if (type) {
    filter.type = type;

    if (type === 'waitlist') {
      filter['waitlist.status'] = 'confirmed';
    }
  }

  if (from) {
    filter.createdAt = { $gte: from };
  }

  if (to) {
    filter.createdAt = { ...filter.createdAt, $lte: to };
  }

  for (const key of [
    'name',
    'referralFrom',
    'waitlist.phoneNumber',
    'waitlist.referralCode',
  ]) {
    if (query[key]) {
      if (key === 'name') {
        filter[key] = { $regex: query[key], $options: 'i' };
      } else {
        filter[key] = query[key];
      }
    }
  }

  return request(
    {
      url: '/userInteraction',
      params: {
        query: JSON.stringify(filter),
        pageSize: per_page,
        page,
        sort,
        hideDeletedUsers,
        hideAnonymousUsers: !!userAnonymous,
        filterReferral,
      },
    },
    sources
  );
};

const getInteractionStatistics = (params, sources) =>
  request({ url: '/getInteractionStatistics', params }, sources);

const getPicksStatistics = async interactionId => {
  try {
    const { currentGroup } = store.getState().auth;

    const answer = await http
      .get(`/picksAnalytics`, {
        params: {
          group: currentGroup._id,
          interaction: interactionId,
        },
      })
      .get('data');

    return answer;
  } catch (err) {
    return null;
  }
};

const stopScreens = (interactionId, data, sources) =>
  request({ url: `/stopScreens/${interactionId}`, method: 'POST', data }, sources);

const stopInteraction = (interactionId, data, sources) =>
  request({ url: `/interaction/stop/${interactionId}`, method: 'POST', data }, sources);

const getInteractions = (params, sources) =>
  request({ url: '/interactions', params }, sources);

const createInteraction = (data, sources) =>
  request({ url: '/interaction', method: 'POST', data }, sources);

const sendMessage = (data, sources) =>
  request({ url: '/channel/event/message', method: 'POST', data }, sources);

/*** LEAGUES ***/
const getLeagues = (params, sources, label) =>
  request({ url: '/league', params }, sources, label);

const createLeague = (data, sources) =>
  request({ url: '/league', method: 'POST', data }, sources);

const getLeague = (id, params, sources, label) =>
  request({ url: `/league/${id}`, params }, sources, label);

const editLeague = (id, data, sources) =>
  request({ url: `/league/${id}`, method: 'PUT', data }, sources);

const editLeaguePosition = (id, data, sources) =>
  request({ url: `/league/reorder/${id}`, method: 'PUT', data }, sources);

const deleteLeague = (id, sources) =>
  request({ url: `/league/${id}`, method: 'DELETE' }, sources);

const getTeams = (params, sources, label) =>
  request({ url: '/sportsapi/teamprofiles', params }, sources, label);

const editTeam = async (teamId, team) => {
  if (teamId && team) {
    try {
      const answer = await http.put(`/sportsapi/teamprofile/${teamId}`, team);
      return answer;
    } catch (err) {
      return null;
    }
  } else {
    return null;
  }
};

const deleteTeam = async teamId => {
  if (teamId) {
    try {
      const answer = await http.delete(`/sportsapi/teamprofile/${teamId}`);
      return answer;
    } catch (err) {
      return null;
    }
  } else {
    return null;
  }
};

const createTeam = async newTeam => {
  try {
    const answer = await http.post('/sportsapi/teamprofiles', newTeam);
    return answer;
  } catch (err) {
    return null;
  }
};

/*** GAMES ***/
const createGame = (data, sources, label) =>
  request({ url: '/sportsapi/game', method: 'POST', data }, sources, label);

const getGame = (id, params, sources, label) =>
  request({ url: `/sportsapi/game/${id}`, params }, sources, label);

const editGame = (id, data, sources) =>
  request({ url: `/sportsapi/game/${id}`, method: 'PUT', data }, sources);

const deleteGame = id => request({ url: `/sportsapi/game/${id}`, method: 'DELETE' });

const getSchedule = async query => {
  try {
    const answer = await http
      .get('/sportsapi/schedule', {
        params: {
          ...query,
        },
      })
      .get('data');
    return answer;
  } catch (err) {
    return null;
  }
};

const sendPushMobileConnected = (eventId, sources) =>
  request(
    {
      url: `/v1/event/mobileconnected/push/${eventId}`,
      method: 'POST',
    },
    sources
  );

const checkMobileConnection = (eventId, sources) =>
  request({ url: `/v1/event/mobileconnected/${eventId}` }, sources);

const getSports = (params, sources) => request({ url: '/sport/list', params }, sources);

const createSport = async newSport => {
  try {
    const answer = await http.post('/sport', newSport);
    return answer;
  } catch (err) {
    return null;
  }
};

const editSport = async (sportId, sport) => {
  if (sportId && sport) {
    try {
      const answer = await http.put(`/sport/${sportId}`, sport);
      return answer;
    } catch (err) {
      return null;
    }
  } else {
    return null;
  }
};

const deleteSport = async sportId => {
  if (sportId) {
    try {
      const answer = await http.delete(`/sport/${sportId}`);
      return answer;
    } catch (err) {
      return null;
    }
  } else {
    return null;
  }
};

const getChannel = (id, sources) => request({ url: `/channel/${id}` }, sources);

const getChannels = (params, sources) => request({ url: '/channel', params }, sources);

const createChannel = (data, sources) =>
  request({ url: '/channel', method: 'POST', data }, sources);

const editChannel = (id, data, sources) =>
  request({ url: `/channel/${id}`, method: 'PUT', data }, sources);

const deleteChannel = (id, sources) =>
  request({ url: `/channel/${id}`, method: 'DELETE' }, sources);

const createChannelEvent = (data, sources) =>
  request({ url: '/channelEvent', method: 'POST', data }, sources);

const updateChannelEvent = (id, data, sources) =>
  request({ url: `/channelEvent/${id}`, method: 'PUT', data }, sources);

const getChannelEvent = (id, params, sources) =>
  request({ url: `/channelEvent/${id}`, params }, sources);

const deleteChannelEvent = async eventId => {
  if (eventId) {
    try {
      const answer = await http.delete(`/channelEvent/${eventId}`);
      return answer;
    } catch (err) {
      return null;
    }
  } else {
    return null;
  }
};

const getChannelEvents = (params, sources) =>
  request({ url: '/channelEvent', params }, sources);

const stopChannelEvent = (id, sources) =>
  request({ url: `/channel/event/stop/${id}`, method: 'PUT' }, sources);

/*** TEMPLATES ***/
const createTemplate = (data, sources) =>
  request({ url: '/template', method: 'POST', data }, sources);

const getTemplate = (id, params, sources) =>
  request({ url: `/template/${id}`, params }, sources);

const deleteTemplate = (id, sources) =>
  request({ url: `/template/${id}`, method: 'DELETE' }, sources);

const editTemplate = (id, data, sources) =>
  request({ url: `/template/${id}`, method: 'PUT', data }, sources);

const copyTemplate = (id, sources) =>
  request({ url: `/copyTemplate/${id}`, method: 'POST' }, sources);

const getTemplates = (params, sources) => request({ url: '/template', params }, sources);

const getFiles = async query => {
  try {
    const answer = await http
      .get('/files', {
        params: {
          ...query,
        },
      })
      .get('data');
    return answer;
  } catch (err) {
    return null;
  }
};

const getUsers = (params, sources) => request({ url: '/users', params }, sources);

const getUser = async userId => {
  if (userId) {
    try {
      const answer = await http.get(`/users/${userId}`).get('data');
      return answer;
    } catch (err) {}
  }
  return null;
};

const createUser = (data, sources) =>
  request({ url: '/users', method: 'POST', data }, sources);

const getGroup = (id, sources) => request({ url: `/group/${id}` }, sources);

const getGroups = (params, sources) => request({ url: '/group', params }, sources);

const createGroup = (data, sources) =>
  request({ url: '/group', method: 'POST', data }, sources);

const editGroup = (data, sources) =>
  request({ url: '/group', method: 'PUT', data }, sources);

const getLeads = async query => {
  try {
    const answer = await http
      .get('/leads', {
        params: {
          ...query,
        },
      })
      .get('data');
    return answer;
  } catch (err) {
    return null;
  }
};

const getImageLibraryList = (params, sources) =>
  request({ url: '/imageLibrary/listImages', params }, sources);

const getImagesTable = async query => {
  try {
    const answer = await http
      .get('/imageLibrary/listImagesTable', {
        params: {
          ...query,
        },
      })
      .get('data');
    return answer;
  } catch (err) {
    return null;
  }
};

const deleteImageLibrary = async imageId => {
  if (imageId) {
    try {
      const answer = await http.delete(`/imageLibrary/deleteImage/${imageId}`);
      return answer;
    } catch (err) {
      return null;
    }
  } else {
    return null;
  }
};

const createTag = (data, sources) =>
  request({ url: '/tags', method: 'POST', data }, sources);

const deleteTags = (data, sources) =>
  request({ url: '/tags', method: 'DELETE', data }, sources);

const getTagsByUser = (params, sources) =>
  request({ url: '/tagsByUser', params }, sources);

const getStatisticsByUser = (params, sources) =>
  request({ url: '/userInteractionStatistics', params }, sources);

const getEventStatistics = async (eventId, query) => {
  try {
    const answer = await http
      .get(`/eventStatistics/${eventId}`, {
        params: {
          ...query,
        },
      })
      .get('data');
    return answer;
  } catch (err) {
    return null;
  }
};

const getTagCategories = sources => request({ url: 'tagCategories' }, sources);

const getTagsByCategory = (category, sources) =>
  request({ url: `/tagsByCategory/${category}` }, sources);

const updatePassword = (data, sources) =>
  request({ url: '/updateUsers', method: 'POST', data }, sources);

const getKpiRealtime = async query => {
  if (query) {
    try {
      const answer = await http.post('/google/reportRealtime', query).get('data');
      return answer;
    } catch (err) {
      return null;
    }
  }
  return null;
};

const getGoogleReport = async query => {
  if (query) {
    try {
      const answer = await http.post('/google/report', query).get('data');
      return answer;
    } catch (err) {
      return null;
    }
  }
  return null;
};

const getUserPoints = (userId, sources) => request({ url: `/points/${userId}` }, sources);

const getBracketAnalytics = async query => {
  if (query) {
    try {
      const answer = await http.post('/bracketAnalytics', query).get('data');
      return answer;
    } catch (err) {
      return null;
    }
  }
  return null;
};

const getOnboardingAnalytics = async query => {
  try {
    const answer = await http
      .get('/onboardingAnalytics', {
        params: {
          ...query,
        },
      })
      .get('data');
    return answer;
  } catch (err) {
    return null;
  }
};

const getOnboardingUsers = async query => {
  try {
    const answer = await http
      .get('/onboardingUsers', {
        params: {
          ...query,
        },
      })
      .get('data');
    return answer;
  } catch (err) {
    return null;
  }
};

const getUsersAnalytics = (params, sources) =>
  request({ url: '/usersAnalytics', params }, sources);

const getInfluencerAnalytics = (params, sources) =>
  request({ url: `/influencerAnalytics`, params }, sources);

const getAgencyAnalytics = (params, sources) =>
  request({ url: `/agencyAnalytics`, params }, sources);

const getTeamAnalytics = async query => {
  try {
    const answer = await http
      .get('/teamAnalytics', {
        params: {
          ...query,
        },
      })
      .get('data');
    return answer;
  } catch (err) {
    return null;
  }
};

const getUsersWithEvent = (data, sources) =>
  request({ url: '/listUsersWithEvent', method: 'POST', data }, sources);

const createSegment = (data, sources) =>
  request({ url: '/segment', method: 'POST', data }, sources);

const getPlayers = async query => {
  try {
    const answer = await http
      .get('/sportsapi/playerprofiles', {
        params: {
          ...query,
        },
      })
      .get('data');
    return answer;
  } catch (err) {
    return null;
  }
};

const getTasks = async () => {
  try {
    return await http.get('/tasks').get('data');
  } catch (err) {}
};

const startTask = async payload => {
  if (payload) {
    try {
      return await http.post(`/tasks/start`, payload).get('data');
    } catch (err) {}
  }
};

const stopTask = async payload => {
  if (payload) {
    try {
      return await http.post('/tasks/stop', payload).get('data');
    } catch (err) {}
  }
};

const runTask = async payload => {
  if (payload) {
    try {
      return await http.post('/tasks/run', payload).get('data');
    } catch (err) {}
  }
};

const startPush = (data, sources) =>
  request({ url: '/push/start', method: 'POST', data }, sources);

const stopPush = (data, sources) =>
  request({ url: '/push/stop', method: 'POST', data }, sources);

const getPlays = (gameId, sources) =>
  request({ url: `/channel/event/game/play/${gameId}` }, sources);

const getPlayCategories = (leagueId, sources) =>
  request(
    {
      url: `/sportsapi/dictionary/${leagueId}`,
      params: { filter: 'play.category' },
    },
    sources
  );

const updateEventPlayCategories = (eventId, categories, sources) =>
  request(
    {
      url: '/channel/event/play',
      method: 'PUT',
      data: { eventId, categories },
    },
    sources
  );

const getAgencyCodes = async query => {
  try {
    const answer = await http.get('/referralCode', { params: { ...query } }).get('data');

    return answer;
  } catch (err) {
    return null;
  }
};

const createAgencyCode = async data => {
  const { currentGroup } = store.getState().auth;

  if (data) {
    try {
      return await http.post('/referralCode', {
        ...data,
        group: currentGroup._id,
      });
    } catch (err) {
      return err.response.data;
    }
  }

  return null;
};

const activePauseAgencyCode = async referralId => {
  const { currentGroup } = store.getState().auth;

  if (referralId) {
    try {
      return await http.post(`/referralCode/active/${referralId}`, {
        group: currentGroup._id,
      });
    } catch (err) {
      return err.response.data;
    }
  }

  return null;
};

const getRugbyBclcAnalytics = (params, sources) =>
  request({ url: '/rugbyBclc', params }, sources);

const getUserChannelAnalytics = (params, sources) =>
  request({ url: '/userChannelStatistics', params }, sources);

const getSingleUserInteraction = (userId, sources) =>
  request({ url: `/userInteractions/${userId}` }, sources);

const getTwitterAccounts = (params, sources) =>
  request({ url: `/twitter`, params }, sources);

const updateTwitterAccounts = (data, sources) =>
  request({ url: '/twitter', method: 'POST', data }, sources);

const loadFileBase64 = file =>
  file
    ? new Promise(resolve => {
        const reader = new FileReader();

        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);

        reader.readAsDataURL(file);
      })
    : Promise.resolve(null);

export const file = {
  loadFileBase64,
  uploadFileBase64: async (endpoint, file, params, sources) => {
    const data = await loadFileBase64(file);

    if (data) {
      const result = await request(
        {
          url: endpoint,
          method: 'POST',
          data: { ...params, data, type: file.type, name: file.name },
        },
        sources
      );

      return result;
    }

    return { error: 'Could not load the file' };
  },
  uploadFileMultipart: (endpoint, file, params, sources) => {
    const data = new FormData();

    for (const key in _.omitBy(params, _.isUndefined)) {
      data.append(key, params[key]);
    }

    data.append('file', file, file.name);
    data.append('type', file.type);
    data.append('name', file.name);

    return request(
      {
        url: endpoint,
        method: 'POST',
        data,
        headers: { 'Content-Type': 'multipart/form-data' },
      },
      sources
    );
  },
  deleteFile: (endpoint, fileId, sources) =>
    request({ url: endpoint, method: 'DELETE', data: { id: fileId } }, sources),
  getFileUrl: (fileId, sources) => request({ url: `/fileUrl/${fileId}` }, sources),
};

const getInboxMessages = (channelId, params, sources) =>
  request({ url: `/inbox/${channelId}`, params }, sources);

const getInboxSentMessages = (userId, params, sources) =>
  request({ url: `/inbox/sent/${userId}`, params }, sources);

const getTopicCategories = sources => request({ url: '/topicCategories' }, sources);

const getTopicsByCategory = (params, sources) =>
  request({ url: '/topicsByCategory', params }, sources);

const getChatsWithTopics = (data, sources) =>
  request({ url: '/listChatsWithTopics', method: 'POST', data }, sources);

/*** MESSAGE FLOW ***/
const getMessageFlows = (params, sources) => request({ url: '/flow', params }, sources);

const getMessageFlow = (id, params, sources) =>
  request({ url: `/flow/${id}`, params }, sources);

const createMessageFlow = (data, sources) =>
  request({ url: '/flow', method: 'POST', data }, sources);

const updateMessageFlow = (id, data, sources) =>
  request({ url: `/flow/${id}`, method: 'PUT', data }, sources);

const deleteFlow = (id, sources) =>
  request({ url: `/flow/${id}`, method: 'DELETE' }, sources);

const copyFlow = (id, sources) =>
  request({ url: `/copyFlow/${id}`, method: 'POST' }, sources);

export const draw = {
  getDraws: (params, sources) => request({ url: '/draw', params }, sources),
  getDraw: (id, params, sources) => request({ url: `/draw/${id}`, params }, sources),

  getDrawParticipants: (drawId, params, sources) =>
    request({ url: `/draw/${drawId}/participants`, params }, sources),

  createDraw: (data, sources) => request({ url: '/draw', method: 'POST', data }, sources),
  deleteDraw: (id, sources) => request({ url: `/draw/${id}`, method: 'DELETE' }, sources),

  updateDraw: (id, data, sources) =>
    request({ url: `/draw/${id}`, method: 'PUT', data }, sources),

  promoteDraw: (id, sources) =>
    request({ url: '/draw/next', method: 'POST', data: { id } }, sources),

  runDraw: (id, sources) =>
    request({ url: '/draw/run', method: 'POST', data: { id } }, sources),

  redeemTicket: (drawId, ticketId, value, sources) =>
    request(
      { url: '/draw/redeem', method: 'POST', data: { drawId, ticketId, value } },
      sources
    ),

  publishResults: (drawId, sources) =>
    request({ url: '/draw/publish', method: 'POST', data: { id: drawId } }, sources),
};

export default {
  getUserInteractions,
  getInteractionStatistics,
  getPicksStatistics,
  stopScreens,
  stopInteraction,
  getInteractions,
  createInteraction,
  sendMessage,
  getLeagues,
  createLeague,
  getLeague,
  editLeague,
  editLeaguePosition,
  deleteLeague,
  getTeams,
  editTeam,
  deleteTeam,
  createTeam,
  createGame,
  editGroup,
  getGame,
  editGame,
  deleteGame,
  getSchedule,
  sendPushMobileConnected,
  checkMobileConnection,
  getSports,
  createSport,
  editSport,
  deleteSport,
  getChannel,
  getChannels,
  deleteChannel,
  createChannelEvent,
  updateChannelEvent,
  getChannelEvent,
  deleteChannelEvent,
  getChannelEvents,
  stopChannelEvent,
  createChannel,
  editChannel,
  getTemplate,
  getTemplates,
  editTemplate,
  deleteTemplate,
  createTemplate,
  copyTemplate,
  getFiles,
  getUsers,
  getUser,
  createUser,
  createGroup,
  getGroup,
  getGroups,
  getLeads,
  getImageLibraryList,
  getImagesTable,
  deleteImageLibrary,
  createTag,
  deleteTags,
  getTagsByUser,
  getStatisticsByUser,
  getEventStatistics,
  getTagCategories,
  updatePassword,
  getKpiRealtime,
  getGoogleReport,
  getUserPoints,
  getBracketAnalytics,
  getOnboardingAnalytics,
  getOnboardingUsers,
  getInfluencerAnalytics,
  getAgencyAnalytics,
  getUsersAnalytics,
  getTeamAnalytics,
  getUsersWithEvent,
  getTagsByCategory,
  createSegment,
  getPlayers,
  getTasks,
  startTask,
  stopTask,
  runTask,
  startPush,
  stopPush,
  getPlays,
  getPlayCategories,
  updateEventPlayCategories,
  getAgencyCodes,
  createAgencyCode,
  getRugbyBclcAnalytics,
  getUserChannelAnalytics,
  activePauseAgencyCode,
  getSingleUserInteraction,
  getTwitterAccounts,
  updateTwitterAccounts,
  ...file,
  getInboxMessages,
  getInboxSentMessages,
  getTopicCategories,
  getTopicsByCategory,
  getChatsWithTopics,
  getMessageFlows,
  getMessageFlow,
  createMessageFlow,
  updateMessageFlow,
  deleteFlow,
  copyFlow,
  ...draw,
};
